import { unwrap, type Api } from "../api/index.js";
import type { ProductTile } from "@kovernow/types/tenant-web-config";
import type { VaultSummaryData } from "../home/index.js";
import { collectorItemName } from "../add_item/collector.js";

/**
 * Vault (W-3), headless: the API calls, query keys, the item status →
 * badge mapping, client-side filter and search (as Flutter's
 * `vault_cubit.dart:70-112`), URL params, and the photo / name / value /
 * credit view models. One `GET /items/list` load; everything else is local.
 */

// ---------------------------------------------------------------- data

export const ITEM_STATUSES = [
  "pending_otp",
  "pending_sanction",
  "rejected_otp",
  "rejected_sanction",
  "pending_underwriter_review",
  "approved_underwriter_review",
  "rejected_underwriter_review",
  "uninsured",
  "insured",
  "expired",
  "claiming",
  "discontinued",
] as const;

export type ItemStatus = (typeof ITEM_STATUSES)[number];

export interface ItemDocument {
  url: string;
  classification: string;
  order_index?: number;
  type?: string;
  location?: string;
}

/** The `GET /items/list` fields the Vault reads. */
export interface VaultItem {
  _id: string;
  archived: boolean;
  status?: string;
  classification?: string;
  name?: string;
  brand?: string;
  model?: string;
  type?: string;
  valueAmount?: number;
  valueCurrency?: string;
  currentInsuredValue?: number;
  policyId?: string;
  documents?: ItemDocument[];
  /** Collector items: the form's attributes (the name comes from them). */
  attributes?: Record<string, unknown> | null;
  // Item detail and edit (W-6): every PUT starts from the stored item.
  classId?: number | null;
  purchasedAt?: string | null;
  condition?: string;
  listAccessory?: string[];
  isBrand?: boolean;
  universalId?: string;
  notes?: string;
  serialNumber?: string;
  userId?: string;
}

/** `POST /items/validate`: always HTTP 200 with a code. */
export interface ValidateResult {
  code: string;
  title: string;
  message: string;
}

/** The `GET /policy/:policyId` field the item detail reads (SG only). */
export interface ItemPolicyView {
  extensible: boolean;
}

export interface PremiumCreditView {
  amount: number;
  currency: string;
  usable: boolean;
}

export const vaultQueryKeys = {
  all: ["vault"] as const,
  items: () => ["vault", "items"] as const,
  credit: () => ["vault", "credit"] as const,
  item: (id: string) => ["vault", "item", id] as const,
  policy: (policyId: string) => ["vault", "policy", policyId] as const,
};

export interface VaultApi {
  /**
   * GET /items/list, once. All three query keys are sent: openapi-fetch
   * drops nulls and Fastify 400s a missing key. The server ignores
   * `categories`/`archived` and treats an empty `searchString` as no search,
   * so this returns every item, archived ones included.
   */
  list(): Promise<VaultItem[]>;
  /** PUT /items/archive (owner-scoped on the server). */
  setArchived(id: string, archived: boolean): Promise<void>;
  /** GET /premium-credit: the caller's own AXA balance. */
  credit(): Promise<PremiumCreditView>;
  /** GET /items/:id (404 when missing or not the caller's). */
  get(id: string): Promise<VaultItem>;
  /** PUT /items: returns the stored item after the update. */
  update(payload: { id: string }): Promise<VaultItem>;
  /** DELETE /items/:id (409 `item_has_policy` once the item has had cover). */
  remove(id: string): Promise<void>;
  /** POST /items/validate {id}. */
  validate(id: string): Promise<ValidateResult>;
  /**
   * GET /policy/:policyId (the human policy number, as `item.policyId`
   * holds). SG only: HK's `/policy` has no `extensible`, so HK makes no
   * call and gets null.
   */
  policy(policyId: string): Promise<ItemPolicyView | null>;
}

export function createVaultApi(api: Api): VaultApi {
  const c = api.shared;
  return {
    async list() {
      const r = await unwrap(
        c.GET("/api/sg/items/list", {
          params: { query: { categories: "", archived: false, searchString: "" } },
        }),
      );
      return r.data.items.filter((i) => i.prospectType !== "trip") as VaultItem[];
    },
    async setArchived(id, archived) {
      await unwrap(c.PUT("/api/sg/items/archive", { body: { id, archived } }));
    },
    async credit() {
      const r = await unwrap(c.GET("/api/sg/premium-credit/", {}));
      return { amount: r.data.amount, currency: r.data.currency, usable: r.data.usable };
    },
    async get(id) {
      const r = await unwrap(c.GET("/api/sg/items/{id}", { params: { path: { id } } }));
      return r.data as unknown as VaultItem;
    },
    async update(payload) {
      // The DTO types `documents[].type` and `attributes` loosely; the payload fits it.
      const r = await unwrap(c.PUT("/api/sg/items/", { body: payload as never }));
      return r.data as unknown as VaultItem;
    },
    async remove(id) {
      await unwrap(c.DELETE("/api/sg/items/{id}", { params: { path: { id } } }));
    },
    async validate(id) {
      const r = await unwrap(c.POST("/api/sg/items/validate", { body: { id } }));
      return { code: r.data.code, title: r.data.title, message: r.data.message };
    },
    async policy(policyId) {
      const rc = api.regionClient;
      if (rc.region !== "sg") return null;
      const r = await unwrap(rc.client.GET("/api/sg/policy/{id}", { params: { path: { id: policyId } } }));
      return { extensible: r.data.policy.extensible === true };
    },
  };
}

// ---------------------------------------------------------------- status

/** The badge an item shows (`status.<display>` i18n keys). */
export type DisplayStatus = "uninsured" | "expired" | "active" | "pending" | "failed";

export interface DisplayState {
  status: DisplayStatus;
  /** `claiming`: Active plus `vault.claimNote` under the badge. */
  claimNote: boolean;
}

/** Item `status` → badge. Unknown or missing reads as Uninsured. */
export function toDisplayStatus(status: string | undefined): DisplayState {
  const s = status ?? "";
  if (s === "insured") return { status: "active", claimNote: false };
  if (s === "claiming") return { status: "active", claimNote: true };
  if (s === "expired" || s === "discontinued") return { status: "expired", claimNote: false };
  if (s.startsWith("pending_")) return { status: "pending", claimNote: false };
  if (s.startsWith("rejected_")) return { status: "failed", claimNote: false };
  return { status: "uninsured", claimNote: false };
}

/**
 * Not covered = the item's id is in `dashboardData.vault.uncoveredItemIds`
 * (the server's policy join, the same set as `uninsuredCount`). While the
 * dashboard is loading or failed (`uncovered` null/undefined), or for an
 * archived item (never in the server's set), fall back to the badge:
 * Uninsured or Expired.
 */
export function isNotCovered(
  item: Pick<VaultItem, "_id" | "status" | "archived">,
  uncovered: ReadonlySet<string> | null | undefined,
): boolean {
  // The server's set never lists archived items: they use the badge.
  if (uncovered && !item.archived) return uncovered.has(item._id);
  const d = toDisplayStatus(item.status).status;
  return d === "uninsured" || d === "expired";
}

/** The ids from `dashboardData.vault`, or null when it has none (older server). */
export function uncoveredSet(vault: Pick<VaultSummaryData, "uncoveredItemIds"> | null | undefined): Set<string> | null {
  return vault?.uncoveredItemIds ? new Set(vault.uncoveredItemIds) : null;
}

// ---------------------------------------------------------------- URL params

export const STATUS_FILTERS = ["all", "uninsured", "active", "pending", "failed"] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

/** Every classification the backend knows ("unknown" = the Other bucket). */
export const ITEM_CLASSIFICATIONS = [
  "watch",
  "camera",
  "laptop",
  "jewellery",
  "handbag",
  "art",
  "wine",
  "whisky",
  "tradingCard",
  "unknown",
] as const;

const KNOWN_CLASSIFICATIONS: ReadonlySet<string> = new Set(ITEM_CLASSIFICATIONS.filter((c) => c !== "unknown"));

export interface VaultParams {
  q: string;
  status: StatusFilter;
  /** "all", a classification, or "unknown" (Other). */
  category: string;
  hidden: boolean;
}

export const DEFAULT_VAULT_PARAMS: VaultParams = { q: "", status: "all", category: "all", hidden: false };

/** `?q=&status=&category=&hidden=1`; any unknown value falls back to its default. */
export function parseVaultParams(search: URLSearchParams | string): VaultParams {
  const p = typeof search === "string" ? new URLSearchParams(search) : search;
  const status = p.get("status") ?? "";
  const category = p.get("category") ?? "";
  return {
    q: p.get("q") ?? "",
    status: (STATUS_FILTERS as readonly string[]).includes(status) ? (status as StatusFilter) : "all",
    category: (ITEM_CLASSIFICATIONS as readonly string[]).includes(category) ? category : "all",
    hidden: p.get("hidden") === "1",
  };
}

/** Only non-default values are written, so a clean vault is plain `/vault`. */
export function toVaultParams(params: VaultParams): URLSearchParams {
  const out = new URLSearchParams();
  if (params.q) out.set("q", params.q);
  if (params.status !== "all") out.set("status", params.status);
  if (params.category !== "all") out.set("category", params.category);
  if (params.hidden) out.set("hidden", "1");
  return out;
}

export function hasActiveFilters(params: VaultParams): boolean {
  return params.q.trim() !== "" || params.status !== "all" || params.category !== "all";
}

export const SEARCH_DEBOUNCE_MS = 250;

// ---------------------------------------------------------------- view models

/** A classification as a category bucket: unrecognised or missing → "unknown". */
export function categoryOf(item: Pick<VaultItem, "classification">): string {
  const c = item.classification ?? "";
  return KNOWN_CLASSIFICATIONS.has(c) ? c : "unknown";
}

/** `home.tiles.<c>` for a known classification, else `vault.category.unknown`. */
export function categoryLabelKey(classification: string | undefined): string {
  const c = classification ?? "";
  return KNOWN_CLASSIFICATIONS.has(c) ? `home.tiles.${c}` : "vault.category.unknown";
}

/** The `classification === "item"` document with the lowest `order_index`. Never `documents[].type`. */
export function itemPhoto(item: Pick<VaultItem, "documents">): string | undefined {
  let best: ItemDocument | undefined;
  for (const d of item.documents ?? []) {
    if (d.classification !== "item" || !d.url) continue;
    if (!best || (d.order_index ?? Infinity) < (best.order_index ?? Infinity)) best = d;
  }
  return best?.url;
}

/**
 * `name`, else brand + model, else a collector item's name from its
 * attributes, else the category label. Returns the literal name or, for the
 * fallback, the i18n key for the screen to translate.
 */
export function itemName(item: Pick<VaultItem, "name" | "brand" | "model" | "classification" | "attributes">):
  | { text: string }
  | { labelKey: string } {
  const name = item.name?.trim();
  if (name) return { text: name };
  const bm = [item.brand?.trim(), item.model?.trim()].filter(Boolean).join(" ");
  if (bm) return { text: bm };
  const collector = collectorItemName(item.classification, item.attributes);
  if (collector) return { text: collector };
  return { labelKey: categoryLabelKey(item.classification) };
}

/** `currentInsuredValue` when above 0, else `valueAmount`. */
export function displayValue(item: Pick<VaultItem, "currentInsuredValue" | "valueAmount">): number | undefined {
  const insured = Number(item.currentInsuredValue);
  if (Number.isFinite(insured) && insured > 0) return insured;
  const value = Number(item.valueAmount);
  return item.valueAmount != null && Number.isFinite(value) ? value : undefined;
}

/** A missing `valueCurrency` counts as the tenant currency. */
export function inTenantCurrency(item: Pick<VaultItem, "valueCurrency">, currency: string): boolean {
  return !item.valueCurrency || item.valueCurrency === currency;
}

/** Sum of display values for non-archived items in the tenant currency. */
export function vaultTotal(items: readonly VaultItem[], currency: string): number {
  const sum = items.reduce((acc, i) => {
    if (i.archived || !inTenantCurrency(i, currency)) return acc;
    const v = displayValue(i);
    return acc + (v != null && v > 0 ? v : 0);
  }, 0);
  return Math.round(sum * 100) / 100;
}

/** "{value} unprotected": non-archived, Not covered, tenant currency, value > 0. */
export function valueAtRisk(item: VaultItem, notCovered: boolean, currency: string): number | undefined {
  if (item.archived || !notCovered || !inTenantCurrency(item, currency)) return undefined;
  const v = displayValue(item);
  return v != null && v > 0 ? v : undefined;
}

export interface CategoryOption {
  value: string;
  /** i18n key: `home.tiles.<c>` or `vault.category.unknown`. */
  labelKey: string;
}

/**
 * The tenant tiles that passed `resolveProductTiles` (config order), then
 * any other classification present in the items, then "Other" when an item
 * has an unknown or missing classification.
 */
export function categoryOptions(
  tiles: ReadonlyArray<{ category: string }>,
  items: readonly VaultItem[],
): CategoryOption[] {
  const seen = new Set<string>();
  const out: CategoryOption[] = [];
  const add = (c: string) => {
    if (seen.has(c)) return;
    seen.add(c);
    out.push({ value: c, labelKey: categoryLabelKey(c) });
  };
  for (const t of tiles) if (KNOWN_CLASSIFICATIONS.has(t.category)) add(t.category);
  let other = false;
  for (const i of items) {
    const c = categoryOf(i);
    if (c === "unknown") other = true;
    else add(c);
  }
  if (other) add("unknown");
  return out;
}

/**
 * The AXA credit balance to show on a card, or undefined for no chip:
 * - the item is Not covered and its badge is Uninsured or Expired,
 * - its classification's tile (unfiltered by flags) is AXA — an unknown
 *   classification gets no chip,
 * - the credit is usable, above 0 and in the tenant currency.
 */
export function creditChipFor(
  item: VaultItem,
  opts: {
    notCovered: boolean;
    tiles: readonly ProductTile[] | undefined;
    credit: PremiumCreditView | null | undefined;
    currency: string;
  },
): number | undefined {
  const { credit } = opts;
  if (!credit || !credit.usable || !(credit.amount > 0) || credit.currency !== opts.currency) return undefined;
  if (item.archived || !opts.notCovered) return undefined;
  const d = toDisplayStatus(item.status).status;
  if (d !== "uninsured" && d !== "expired") return undefined;
  const tile = (opts.tiles ?? []).find((t) => t.category === item.classification);
  if (!tile || tile.underwriter.toLowerCase() !== "axa") return undefined;
  return credit.amount;
}

// ---------------------------------------------------------------- filtering

/** Case-insensitive substring over display name, brand, model and type. */
export function matchesSearch(item: VaultItem, q: string, displayName?: string): boolean {
  const needle = q.trim().toLocaleLowerCase();
  if (!needle) return true;
  const fields = [displayName, item.name, item.brand, item.model, item.type];
  return fields.some((f) => !!f && f.toLocaleLowerCase().includes(needle));
}

export function matchesStatus(item: VaultItem, status: StatusFilter, notCovered: boolean): boolean {
  switch (status) {
    case "all":
      return true;
    case "uninsured":
      return notCovered;
    default:
      return toDisplayStatus(item.status).status === status;
  }
}

/**
 * The cards to show. `hidden` shows archived items only (and the normal view
 * never shows them); search, Status and Category apply in both views.
 */
export function filterItems(
  items: readonly VaultItem[],
  params: VaultParams,
  opts: {
    uncovered: ReadonlySet<string> | null | undefined;
    /** Translated display name (for the category-label fallback). */
    displayName?: (item: VaultItem) => string;
  },
): VaultItem[] {
  return items.filter((i) => {
    if (!!i.archived !== params.hidden) return false;
    if (params.category !== "all" && categoryOf(i) !== params.category) return false;
    if (!matchesStatus(i, params.status, isNotCovered(i, opts.uncovered))) return false;
    return matchesSearch(i, params.q, opts.displayName?.(i));
  });
}

// ---------------------------------------------------------------- banner

/**
 * Home's all-expired rule (`insuredCount` 0, ≥1 lapsed item): one lapsed
 * item → its Choose plan; several → the Not covered filter.
 */
export function expiredBannerRoute(vault: VaultSummaryData | null | undefined): string | undefined {
  if (!vault || vault.insuredCount !== 0 || vault.lapsedItems.length === 0) return undefined;
  if (vault.lapsedItems.length === 1) {
    return `/vault/${encodeURIComponent(vault.lapsedItems[0]!.itemId)}/plan`;
  }
  return "/vault?status=uninsured";
}

export function itemRoute(id: string): string {
  return `/vault/${encodeURIComponent(id)}`;
}

export * from "./item_detail.js";
